import React, { FC, useContext, useMemo, useCallback, HTMLAttributes } from 'react';
import { ThemeContext } from 'styled-components';

import { SCP } from 'ia-react-core/~/interfaces/SCP';
import useTranslation from '../../../hooks/useTranslation';
import { StyledLink, StyledLogo } from './MainLogoHorizontal.styles';
import getLanguageCode from '../../../utils/language/getLanguageCode';

export interface MainLogoHorizontalProps extends HTMLAttributes<HTMLAnchorElement> {
  /** Url of the links that wraps the logo. */
  homePageUrl?: string;
  /** If blue and white are inverted */
  isInverted?: boolean;
}

const MainLogoHorizontalDefaultProps = {
  homePageUrl: '/',
};

const MainLogoHorizontal: FC<MainLogoHorizontalProps> = ({ homePageUrl, isInverted, ...props }) => {
  const { language } = useTranslation();
  const languageCode = useMemo(() => getLanguageCode(language), [language]);
  const { assets, translations } = useContext(ThemeContext);
  const logoTitle = useMemo(() => translations[languageCode].logoTitle, [languageCode, translations]);

  const isCurrentLanguageFrench = useCallback(() => languageCode === 'fr', [languageCode]);

  const logoFr = isInverted ? assets.horizontalLogoInvFr : assets.horizontalLogoFr;
  const logoEn = isInverted ? assets.horizontalLogoInvEn : assets.horizontalLogoEn;

  const TranslatedLogo = useMemo(() => isCurrentLanguageFrench() ? logoFr : logoEn, [logoFr, logoEn, isCurrentLanguageFrench]);

  const Logo = useMemo(() => {
    // To exclude the following props from the final DOM element
    // eslint-disable-next-line
    const ConfiguredLogo = (p: SCP<'svg'> | SCP<'img'>) => (
      <TranslatedLogo {...p} />
    );
    return ConfiguredLogo;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TranslatedLogo]);

  return (
    <StyledLink to={homePageUrl} title={logoTitle} data-testid="MainLogoHorizontal" {...props}>
      <StyledLogo<typeof Logo>
        as={Logo}
        focusable="false"
        alt={logoTitle}
      />
    </StyledLink>
  );
};

MainLogoHorizontal.defaultProps = MainLogoHorizontalDefaultProps;

export default MainLogoHorizontal;
